import React from "react";

import { formatNumericCurrency, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { Flex, Icon, Typography } from "components";

import { PayoutSummaryDonutChart } from "pages/app/field-timeline/components/payout-context-drawer/PayoutSummaryDonutChart";
import { getPayoutProgressState } from "pages/app/field-timeline/utils/PayoutUtils";

type PayoutContextSummaryCardProps = {
  currency?: CurrencyCode;
  actualPayout: number;
  eligiblePayout: number;
  hasNoAllotment: boolean;
};

export const PayoutContextSummaryCard = ({ currency, actualPayout, eligiblePayout, hasNoAllotment }: PayoutContextSummaryCardProps) => {
  const { t } = useTranslation("fieldTimeline");

  const progress = getPayoutProgressState(actualPayout, eligiblePayout);
  const summaryActual = Math.max(actualPayout, 0);
  const expectedPayoutProgress = eligiblePayout > 0 ? Math.min((summaryActual / eligiblePayout) * 100, 100) : 0;
  const doughnutProgressPct = Math.max(0, Math.round((progress?.ratio ?? 0) * 100));
  const statusKey =
    progress?.state === "onTrack"
      ? "impactPanel.payoutImpact.contextDrawer.summary.onTrack"
      : progress?.state === "keepRolling"
      ? "impactPanel.payoutImpact.contextDrawer.summary.keepRolling"
      : progress?.state === "needsAttention"
      ? "impactPanel.payoutImpact.contextDrawer.summary.needsAttention"
      : null;

  return (
    <Flex vertical className="pcd-card pcd-summary-card">
      <Flex className="pcd-summary-layout">
        <Flex align="center" className="pcd-summary-left" gap="0.75rem">
          <PayoutSummaryDonutChart progressPct={expectedPayoutProgress} />
          <Typography variant="h1" className="pcd-summary-amount">
            {formatNumericCurrency(summaryActual, { decimals: 0, currency })}
          </Typography>
        </Flex>

        <div className="pcd-summary-divider" />
        {statusKey && (
          <Flex vertical className="pcd-summary-copy" gap="0.5rem">
            <Flex className="pcd-summary-status" gap="0.25rem">
              <Icon iconName="tabler/arrow-up-right-circle" size="s" />
              <Typography variant="paragraph-s" className="pcd-summary-status-text">
                {t(statusKey)}
              </Typography>
            </Flex>
            <Typography variant="paragraph-s" className="pcd-summary-text">
              {t("impactPanel.payoutImpact.contextDrawer.summary.progressText", { progress: doughnutProgressPct })}
            </Typography>
          </Flex>
        )}
        {hasNoAllotment && (
          <Flex vertical align="center" className="pcd-summary-copy" gap="0.5rem">
            <Typography variant="paragraph-s" className="pcd-summary-text">
              {t("impactPanel.payoutImpact.contextDrawer.summary.noAllotment")}
            </Typography>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
